import { useState, useCallback } from 'react';
import { FieldData } from '@/types/label';

export const useCsvData = () => {
  const [csvData, setCsvData] = useState<FieldData[]>([]);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

  const parseCSV = useCallback((text: string): FieldData[] => {
    let lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    // Skip Excel's sep=, hint if present
    if (lines[0].toLowerCase().startsWith('sep=')) {
      lines = lines.slice(1);
    }

    // Detect delimiter: tab, semicolon, or comma (in order of priority)
    const firstLine = lines[0];
    let delimiter = ',';
    if (firstLine.includes('\t')) {
      delimiter = '\t';
    } else if (firstLine.includes(';')) {
      delimiter = ';';
    }

    // Parse headers
    const headers = firstLine.split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ''));
    setCsvHeaders(headers);

    // Parse rows
    const rows: FieldData[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = delimiter === ',' 
        ? parseCSVLine(line)
        : line.split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
      
      if (values.length === headers.length && values.some(v => v !== '')) {
        const row: FieldData = {};
        headers.forEach((header, idx) => {
          row[header] = values[idx];
        });
        rows.push(row);
      }
    }

    return rows;
  }, []);

  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    return values;
  };

  const importCSV = useCallback((file: File): Promise<FieldData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const data = parseCSV(text);
          setCsvData(data);
          setCurrentRowIndex(0);
          resolve(data);
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }, [parseCSV]);

  /**
   * Parse a key:value text document into one or more records.
   * - Lines `key: value` or `key = value` become fields
   * - `#` or `//` lines are comments
   * - Trailing `\` continues the value onto the next line
   * - In multi mode, blank line (or custom separator) starts a new record
   */
  const parseTextDocument = useCallback(
    (text: string, opts: { mode: 'single' | 'multi'; separator?: string }): { headers: string[]; rows: FieldData[] } => {
      const cleaned = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
      const rawLines = cleaned.split('\n');

      // Stitch line continuations: trailing backslash means "join next line"
      const lines: string[] = [];
      let buffer = '';
      for (const ln of rawLines) {
        if (buffer) {
          buffer += '\n' + ln;
        } else {
          buffer = ln;
        }
        if (buffer.endsWith('\\')) {
          buffer = buffer.slice(0, -1);
          continue;
        }
        lines.push(buffer);
        buffer = '';
      }
      if (buffer) lines.push(buffer);

      const sep = opts.separator?.trim() ?? '';
      const isSeparator = (ln: string): boolean => {
        const t = ln.trim();
        if (opts.mode === 'single') return false;
        if (sep) return t === sep;
        return t === '';
      };

      const records: FieldData[] = [];
      const orderedKeys: string[] = [];
      let current: FieldData = {};
      let currentHasData = false;

      const pushCurrent = () => {
        if (currentHasData) records.push(current);
        current = {};
        currentHasData = false;
      };

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
          if (isSeparator(line)) pushCurrent();
          continue;
        }
        if (trimmed.startsWith('#') || trimmed.startsWith('//')) continue;
        if (isSeparator(line)) {
          pushCurrent();
          continue;
        }

        const m = line.match(/^\s*([^:=]+?)\s*[:=]\s*([\s\S]*?)\s*$/);
        if (!m) continue;
        const key = m[1].trim();
        const value = m[2];
        if (!key) continue;

        if (!orderedKeys.includes(key)) orderedKeys.push(key);
        current[key] = value;
        currentHasData = true;
      }
      pushCurrent();

      return { headers: orderedKeys, rows: records };
    },
    []
  );

  const importText = useCallback(
    (file: File, opts: { mode: 'single' | 'multi'; separator?: string }): Promise<FieldData[]> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            const { headers, rows } = parseTextDocument(text, opts);
            setCsvHeaders(headers);
            setCsvData(rows);
            setCurrentRowIndex(0);
            resolve(rows);
          } catch (err) {
            reject(new Error('Failed to parse text file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },
    [parseTextDocument]
  );

  const getCurrentRow = useCallback((): FieldData | null => {
    if (csvData.length === 0) return null;
    return csvData[currentRowIndex] || null;
  }, [csvData, currentRowIndex]);

  const nextRow = useCallback(() => {
    setCurrentRowIndex((prev) => Math.min(prev + 1, csvData.length - 1));
  }, [csvData.length]);

  const prevRow = useCallback(() => {
    setCurrentRowIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToRow = useCallback((index: number) => {
    setCurrentRowIndex(Math.max(0, Math.min(index, csvData.length - 1)));
  }, [csvData.length]);

  const clearCSV = useCallback(() => {
    setCsvData([]);
    setCsvHeaders([]);
    setCurrentRowIndex(0);
  }, []);

  return {
    csvData,
    csvHeaders,
    currentRowIndex,
    totalRows: csvData.length,
    getCurrentRow,
    nextRow,
    prevRow,
    goToRow,
    importCSV,
    importText,
    clearCSV,
  };
};
